import React from 'react';
import { AppDisplayImage, RenderImagesData, ViewerImage } from './types';
import { useAvatars } from '../../../hooks/useAvatar';

export type AvatarAPIViewerImage<TImage> = {
    imageViewImageUrl: string;
    imageViewImageDescription?: string;
} & TImage;

export type AvatarAPIImagesProviderOwnProps<TImage> = {
    children: (renderImagesData: RenderImagesData<AvatarAPIViewerImage<TImage>>) => React.ReactNode;
    images: AvatarAPIViewerImage<TImage>[];
};

export type AvatarAPIImagesProviderProps<TImage> = AvatarAPIImagesProviderOwnProps<TImage>;

const AvatarAPIImagesProvider = <TImage,>(props: AvatarAPIImagesProviderProps<TImage>): React.ReactNode => {
    const [focusedImageId, setFocusedImageId] = React.useState<string | null>(null);
    const avatars = useAvatars({
        avatarAPIUrls: props.images.map((image) => image.imageViewImageUrl),
    });
    const presentableAvatars = avatars.filter((avatar) => !avatar.isLoadingAvatar && avatar.avatar);
    const getTargetAvatar = (image: AvatarAPIViewerImage<TImage>) => {
        return presentableAvatars.find((avatar) => avatar.avatarAPIUrl === image.imageViewImageUrl);
    };
    const getIsEnabled = (image: AppDisplayImage<AvatarAPIViewerImage<TImage>>): boolean => {
        const targetAvatar = getTargetAvatar(image);
        if (!targetAvatar) return false;

        return true;
    };

    const onPress = (image: AppDisplayImage<AvatarAPIViewerImage<TImage>>) => {
        if (!getIsEnabled(image)) return;

        setFocusedImageId(image.imageId);
    };

    const appDisplayImages: AppDisplayImage<AvatarAPIViewerImage<TImage>>[] = props.images.map((image) => ({
        ...image,
        imageId: image.imageViewImageUrl,
    }));

    const viewerImages: ViewerImage[] = presentableAvatars.map((avatar) => ({
        uri: avatar.avatar?.base64 || '',
        imageId: avatar.avatarAPIUrl!,
        description: props.images.find((image) => image.imageViewImageUrl === avatar.avatarAPIUrl)
            ?.imageViewImageDescription,
        imageFetchUrl: avatar.avatar?.imageFetchUrl || '',
    }));

    const imageIndex = presentableAvatars.findIndex((image) => image.avatarAPIUrl === focusedImageId);
    const renderImagesData: RenderImagesData<AvatarAPIViewerImage<TImage>> = {
        viewerImages,
        appDisplayImages,
        imageIndex,
        isOpen: !!focusedImageId,
        onClose: () => setFocusedImageId(null),
        getIsPressEnabled: getIsEnabled,
        onPress,
    };

    return props.children(renderImagesData);
};

export default AvatarAPIImagesProvider;
